// Turns the signed-note snapshots into a "what changed, and when" list.
//
// Every signed note embeds the patient's whole profile at signing time, so the
// snapshots are an immutable point-in-time series. Comparing each one to the
// previous turns that series into an answer to the question an admin actually
// has ("did this patient's history change between visits?") — which the old
// "show me one past snapshot" dropdown could only answer by memorising one
// screen and then switching to another.

const isBlank = (v) =>
  v === null ||
  v === undefined ||
  v === "" ||
  v === "-" ||
  v === "null" ||
  (typeof v === "string" && (v.trim() === "" || v.trim() === "null"));

const clean = (v) => (isBlank(v) ? null : String(v).trim());

// Same rule as the Vitals card: a height is only meaningful as a pair.
const fmtHeight = (feet, inches) => {
  const hasFeet = !isBlank(feet);
  const hasInches = !isBlank(inches);
  if (!hasFeet && !hasInches) return null;
  return `${hasFeet ? feet : 0}'${hasInches ? inches : 0}"`;
};

const medicationLabel = (m) =>
  [
    m?.medicationId?.name || m?.name,
    m?.dosage ? `- ${m.dosage}` : null,
    m?.frequency ? `(${m.frequency})` : null,
  ]
    .filter(Boolean)
    .join(" ") || null;

/** Single-value fields, compared as text. */
const scalarsOf = (profile) => ({
  Height: fmtHeight(profile?.heightFeet, profile?.heightInches),
  Weight: isBlank(profile?.weight) ? null : `${profile.weight} lbs`,
  "Alcohol consumption": clean(profile?.alcoholConsumption),
  "Tobacco (packs a week)": clean(profile?.tobacoPerWeek),
  "Tobacco (years)": clean(profile?.tobacoAnnual),
  "Sexual history": clean(profile?.sexualHistory),
});

/** List fields, compared as sets of labels. */
const listsOf = (profile, medications) => ({
  Medications: (medications || []).map(medicationLabel).filter(Boolean),
  "Medical conditions": (profile?.medicalConditions || []).filter(Boolean),
  Allergies: (profile?.allergies || []).map((a) => clean(a?.name)).filter(Boolean),
  "Surgical history": (profile?.userSurgical || [])
    .map((s) => clean(s?.name))
    .filter(Boolean),
  "Family history": (profile?.familyHistory || []).filter(Boolean),
  Immunizations: (profile?.userImmunization?.immuniztionName || [])
    .map((i) => clean(i?.name))
    .filter(Boolean),
});

/**
 * @returns {Array<{label: string, from?: string, to?: string, added?: string[],
 *   removed?: string[]}>} one entry per field that differs.
 */
export const diffProfiles = (previous, next) => {
  const changes = [];

  const prevScalars = scalarsOf(previous.profile);
  const nextScalars = scalarsOf(next.profile);
  Object.keys(nextScalars).forEach((label) => {
    const from = prevScalars[label];
    const to = nextScalars[label];
    if (from !== to) changes.push({ label, from, to });
  });

  const prevLists = listsOf(previous.profile, previous.medications);
  const nextLists = listsOf(next.profile, next.medications);
  Object.keys(nextLists).forEach((label) => {
    const before = prevLists[label] || [];
    const after = nextLists[label] || [];
    const added = after.filter((item) => !before.includes(item));
    const removed = before.filter((item) => !after.includes(item));
    if (added.length || removed.length) changes.push({ label, added, removed });
  });

  return changes;
};

/**
 * Builds the newest-first change log.
 *
 * @param points chronological (oldest → newest) list of
 *   { key, title, subtitle, profile, medications }
 */
export const buildChangeLog = (points) =>
  points
    .map((point, index) =>
      index === 0
        ? { ...point, changes: [], isBaseline: true }
        : { ...point, changes: diffProfiles(points[index - 1], point), isBaseline: false }
    )
    .reverse();
